import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { updateDeviceDetails } from '@/services/devices';
import { useDeviceStore } from '@/stores/useDeviceStore';
import { LOG_PREFIX } from '@/config/constants';
import type { Device } from '@/types';

interface DeviceDetailsFormProps {
  device: Device;
}

interface DetailsState {
  first_name: string;
  last_name: string;
  phone_number: string;
  device_name: string;
  serial_number: string;
}

function toFormState(device: Device): DetailsState {
  return {
    first_name: device.first_name ?? '',
    last_name: device.last_name ?? '',
    phone_number: device.phone_number ?? '',
    device_name: device.device_name ?? '',
    serial_number: device.serial_number ?? '',
  };
}

/** Empty string on a nullable column means "cleared" — normalize to null. */
function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

export function DeviceDetailsForm({ device }: DeviceDetailsFormProps) {
  const updateDevice = useDeviceStore((s) => s.updateDevice);
  const [form, setForm] = useState<DetailsState>(() => toFormState(device));
  const [isSaving, setIsSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initial = toFormState(device);
  const hasChanges = (Object.keys(form) as (keyof DetailsState)[]).some(
    (key) => form[key].trim() !== initial[key].trim()
  );
  const deviceNameEmpty = form.device_name.trim() === '';

  function setField(key: keyof DetailsState, value: string) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSave() {
    if (deviceNameEmpty) return;
    setIsSaving(true);
    setSaved(false);
    setError(null);
    const payload = {
      device_name: form.device_name.trim(),
      first_name: nullable(form.first_name),
      last_name: nullable(form.last_name),
      phone_number: nullable(form.phone_number),
      serial_number: nullable(form.serial_number),
    };
    try {
      await updateDeviceDetails(device.id, payload);
      updateDevice(device.id, payload);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error(LOG_PREFIX, 'Failed to update device details:', err);
      setError(
        typeof err === 'object' && err && 'message' in err
          ? String((err as { message: unknown }).message)
          : 'Failed to save device details'
      );
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-500">
        Edit this device&apos;s identity and the assigned user&apos;s details.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-first-name">
            First name
          </label>
          <Input
            id="device-first-name"
            value={form.first_name}
            onChange={(e) => setField('first_name', e.target.value)}
            disabled={isSaving}
            placeholder="—"
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-last-name">
            Last name
          </label>
          <Input
            id="device-last-name"
            value={form.last_name}
            onChange={(e) => setField('last_name', e.target.value)}
            disabled={isSaving}
            placeholder="—"
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-phone">
          Phone number
        </label>
        <Input
          id="device-phone"
          type="tel"
          value={form.phone_number}
          onChange={(e) => setField('phone_number', e.target.value)}
          disabled={isSaving}
          placeholder="+1 555 000 0000"
        />
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-name">
          Device name
        </label>
        <Input
          id="device-name"
          value={form.device_name}
          onChange={(e) => setField('device_name', e.target.value)}
          disabled={isSaving}
          aria-invalid={deviceNameEmpty}
        />
        {deviceNameEmpty && <p className="mt-1 text-xs text-red-600">Device name is required.</p>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-id">
            Device ID
          </label>
          <Input
            id="device-id"
            value={device.device_id}
            readOnly
            disabled
            className="font-mono text-xs"
          />
          <p className="mt-1 text-xs text-gray-400">Set by the device — not editable.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-serial">
            Serial number
          </label>
          <Input
            id="device-serial"
            value={form.serial_number}
            onChange={(e) => setField('serial_number', e.target.value)}
            disabled={isSaving}
            className="font-mono text-xs"
            placeholder="—"
          />
        </div>
      </div>

      <div>
        <label className="mb-1 block text-xs font-medium text-gray-600" htmlFor="device-record-id">
          Internal ID
        </label>
        <Input
          id="device-record-id"
          value={device.id}
          readOnly
          disabled
          className="font-mono text-xs"
        />
        <p className="mt-1 text-xs text-gray-400">Database record ID — not editable.</p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={isSaving || !hasChanges || deviceNameEmpty}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </Button>
        {saved && <span className="text-xs text-green-600">Saved</span>}
      </div>
    </div>
  );
}
